<template>
  <div class="project-appointment-capacities" v-if="tableStateStore">
    <DataTable
      class="p-datatable-sm p-datatable-striped"
      :value="bookableSlots"
      :loading="loading"
      :lazy="true"
      dataKey="_id"
      :rowHover="true"
      filterDisplay="row"
      :filters.sync="tableStateStore.filters"
      stateStorage="local"
      :stateKey="tableStateStore.filterName()"
      @filter="onFilter"
      @state-restore="onStateRestore"
    >
      <template #header>
        <div class="table-header">
          <div class="table-header-left d-flex flex-wrap align-items-center">
            <b-button-group class="mb-2 mr-3">
              <b-button
                variant="outline-secondary"
                size="sm"
                :title="$t('previous')"
                :aria-label="$t('previous')"
                @click.prevent="showPreviousWeek"
              >
                <i class="fa fa-chevron-left" aria-hidden="true"></i>
              </b-button>
              <b-button variant="outline-secondary" size="sm" @click.prevent="showCurrentWeek">
                {{ $t('this_week') }}
              </b-button>
              <b-button
                variant="outline-secondary"
                size="sm"
                :title="$t('next')"
                :aria-label="$t('next')"
                @click.prevent="showNextWeek"
              >
                <i class="fa fa-chevron-right" aria-hidden="true"></i>
              </b-button>
            </b-button-group>
            <div class="mb-2">
              {{ bookableSlots.length }}
              {{ $tc('slot_pluralized', bookableSlots.length) }}
              <span v-if="selectedWeekLabel">{{ selectedWeekLabel }}</span>
            </div>
          </div>
          <div class="table-header-right">
            <button class="btn btn-narrow btn-inverse ml-2 mb-2" @click.prevent="onRemoveFilters">
              <i class="fa fa-remove mr-2"></i>
              {{ $t('remove_filters') }}
            </button>
          </div>
        </div>
      </template>

      <template #empty>
        {{ $t('no_data_found') }}
      </template>
      <template #loading>
        {{ $t('loading') }}
      </template>

      <Column field="date" :header="$t('date')" filterField="slotDateRange" :showFilterMenu="false">
        <template #body="slotProps">
          {{ formatSlotDate(slotProps.data.date) }}
        </template>
        <template #filter="{ filterModel, filterCallback }">
          <Calendar
            v-model="filterModel.range"
            @input="filterCallback()"
            selectionMode="range"
            dateFormat="dd.mm.yy"
            :placeholder="$t('date_range')"
            :showWeek="true"
          />
        </template>
      </Column>

      <Column :header="$t('time')">
        <template #body>
          {{ defaultSlotTimeRange }}
        </template>
        <template #filter></template>
      </Column>

      <Column field="appointmentType" :header="$t('appointmentType')">
        <template #body="slotProps">
          <b-badge :variant="getAppointmentTypeColor(slotProps.data.appointmentType)">
            {{ $t(slotProps.data.appointmentType) }}
          </b-badge>
        </template>
        <template #filter></template>
      </Column>

      <Column :header="$t('employees')">
        <template #body="slotProps">
          {{ formatEmployees(slotProps.data.employees) }}
        </template>
        <template #filter></template>
      </Column>

      <Column v-if="showEmployerColumn" :header="$t('employer')">
        <template #body="slotProps">
          <span v-if="slotProps.data.employer">{{ slotProps.data.employer.name }}</span>
          <span v-else>–</span>
        </template>
        <template #filter></template>
      </Column>

      <Column :headerStyle="{ width: '120px' }">
        <template #body="slotProps">
          <ConfirmButton
            colorVariant="success"
            confirmColorVariant="success"
            :disabled="bookingSlotId === slotProps.data._id"
            :callbackId="slotProps.data"
            :confirmCallback="onConfirmBookSlot"
          >
            <template #buttonText>
              {{ $t('appointmentCapacityTakeOver') }}
            </template>
            <template #header>
              {{ $t('appointmentCapacityTakeOverConfirm') }}
            </template>
            <template #confirmButtonText>
              {{ $t('appointmentCapacityTakeOver') }}
            </template>
          </ConfirmButton>
        </template>
        <template #filter></template>
      </Column>
    </DataTable>
  </div>
</template>

<script>
import { mapActions, mapGetters } from 'vuex';
import DataTable from 'primevue/datatable';
import Column from 'primevue/column';
import Calendar from 'primevue/calendar';
import ConfirmButton from '@/components/ConfirmButton.vue';
import { getAppointmentTypeColor } from '@/helpers/colors';

/** Same window as backend buildDefaultAppointmentInput (Europe/Berlin). */
const DEFAULT_SLOT_TIME_RANGE = '07:30 – 17:30';

const createProjectAppointmentCapacitiesDefaultFilters = () => ({
  slotDateRange: { range: null },
});

export default {
  name: 'ProjectAppointmentCapacities',
  components: {
    DataTable,
    Column,
    Calendar,
    ConfirmButton,
  },
  props: {
    project: {
      type: Object,
      required: true,
    },
  },
  data() {
    return {
      loading: false,
      bookingSlotId: null,
      defaultSlotTimeRange: DEFAULT_SLOT_TIME_RANGE,
      projectAppointmentCapacitiesTableStateConfig: {
        name: `project-appointment-capacities-${this.project.id}`,
        filters: createProjectAppointmentCapacitiesDefaultFilters(),
        sorting: { sortField: 'date', sortOrder: 1 },
        pagination: { page: 0, pageSize: 100 },
      },
    };
  },
  computed: {
    ...mapGetters('auth', ['isGuest', 'getCurrentUser']),
    ...mapGetters(['getAppointmentCapacities', 'getTableState']),
    tableStateStore() {
      return this.getTableState(this.projectAppointmentCapacitiesTableStateConfig.name);
    },
    bookableSlots() {
      return this.getAppointmentCapacities.filter((capacity) => !capacity.appointment);
    },
    showEmployerColumn() {
      return !this.isGuest;
    },
    selectedRangeStart() {
      const range = this.tableStateStore?.filters?.slotDateRange?.range;
      return range?.[0] || null;
    },
    selectedRangeEnd() {
      const range = this.tableStateStore?.filters?.slotDateRange?.range;
      if (range?.[1]) {
        return range[1];
      }
      if (range?.[0]) {
        return range[0];
      }
      return null;
    },
    selectedWeekLabel() {
      const start = this.selectedRangeStart;
      const end = this.selectedRangeEnd;
      if (!start || !end) {
        return '';
      }
      return `${this.$t('calendar_week_short')} ${this.$week(start)} · ${this.$date(
        start
      )}–${this.$date(end)}`;
    },
  },
  methods: {
    ...mapActions([
      'initTableState',
      'fetchBookableAppointmentCapacities',
      'bookAppointmentCapacity',
      'message',
    ]),
    getAppointmentTypeColor,

    defaultWeekRangeArray(baseDate) {
      const weekStart = this.$dayjs(baseDate)
        .startOf('week')
        .startOf('day');
      const weekEnd = weekStart.add(5, 'day').endOf('day');
      return [weekStart.toDate(), weekEnd.toDate()];
    },

    showWeek(baseDate) {
      this.ensureDefaultDateRange();
      this.tableStateStore.filters.slotDateRange.range = this.defaultWeekRangeArray(baseDate);
      this.loadSlots();
    },

    showPreviousWeek() {
      this.showWeek(this.$dayjs(this.selectedRangeStart).subtract(1, 'week'));
    },

    showCurrentWeek() {
      this.showWeek();
    },

    showNextWeek() {
      this.showWeek(this.$dayjs(this.selectedRangeStart).add(1, 'week'));
    },

    ensureDefaultDateRange() {
      if (!this.tableStateStore) {
        return;
      }

      if (!this.tableStateStore.filters.slotDateRange) {
        this.tableStateStore.filters.slotDateRange = { range: null };
      }

      if (
        !this.tableStateStore.filters.slotDateRange.range ||
        !this.tableStateStore.filters.slotDateRange.range[0]
      ) {
        this.tableStateStore.filters.slotDateRange.range = this.defaultWeekRangeArray();
      }
    },

    resolveQueryDateRange() {
      const range = this.tableStateStore?.filters?.slotDateRange?.range;

      if (range && range[0] && range[1]) {
        return {
          dateFrom: this.$dayjs(range[0])
            .startOf('day')
            .toDate(),
          dateTo: this.$dayjs(range[1])
            .endOf('day')
            .toDate(),
        };
      }

      if (range && range[0]) {
        const day = this.$dayjs(range[0]).startOf('day');
        return {
          dateFrom: day.toDate(),
          dateTo: day.endOf('day').toDate(),
        };
      }

      const [dateFrom, dateTo] = this.defaultWeekRangeArray();
      return { dateFrom, dateTo };
    },

    formatSlotDate(date) {
      return this.$dayjs(date).format('dddd, DD.MM.YYYY');
    },

    formatEmployees(employees) {
      if (!employees || employees.length === 0) {
        return '–';
      }
      return employees.map((employee) => `${employee.firstname} ${employee.lastname}`).join(', ');
    },

    async loadSlots() {
      if (!this.project?.id || this.project.id === '_new') {
        return;
      }

      const { dateFrom, dateTo } = this.resolveQueryDateRange();
      this.loading = true;
      try {
        await this.fetchBookableAppointmentCapacities({
          projectId: this.project.id,
          dateFrom,
          dateTo,
        });
      } finally {
        this.loading = false;
      }
    },

    onFilter() {
      const slotDateRange = this.tableStateStore.filters.slotDateRange;
      if (slotDateRange?.range?.[0]) {
        slotDateRange.range[0] = this.$dayjs(slotDateRange.range[0])
          .startOf('day')
          .toDate();
      }
      if (slotDateRange?.range?.[1]) {
        slotDateRange.range[1] = this.$dayjs(slotDateRange.range[1])
          .endOf('day')
          .toDate();
      }
      this.loadSlots();
    },

    onStateRestore(event) {
      this.tableStateStore.restore(event);
      this.ensureDefaultDateRange();
      this.loadSlots();
    },

    onRemoveFilters() {
      const defaultFilters = createProjectAppointmentCapacitiesDefaultFilters();
      defaultFilters.slotDateRange.range = this.defaultWeekRangeArray();

      this.projectAppointmentCapacitiesTableStateConfig.filters = defaultFilters;

      this.tableStateStore.reset();
      this.tableStateStore.filters = defaultFilters;

      this.loadSlots();
    },

    async onConfirmBookSlot(capacity) {
      if (!capacity?._id || !this.project?.id) {
        return;
      }

      this.bookingSlotId = capacity._id;
      try {
        await this.bookAppointmentCapacity({
          appointmentCapacityId: capacity._id,
          projectId: this.project.id,
        });
        this.message({
          message: this.$t('appointmentCapacityTakeOverSuccess'),
          type: 'success',
          lifetime: 3,
        });
        await this.loadSlots();
        this.$emit('booked');
      } catch (err) {
        const errorMessage =
          err?.graphQLErrors?.[0]?.message ||
          err?.message ||
          this.$t('appointmentCapacitySlotNotAvailable');
        this.message({
          message: errorMessage,
          type: 'danger',
          lifetime: 4,
        });
        await this.loadSlots();
      } finally {
        this.bookingSlotId = null;
      }
    },
  },

  async created() {
    await this.initTableState(this.projectAppointmentCapacitiesTableStateConfig);
    this.ensureDefaultDateRange();
  },

  mounted() {
    this.loadSlots();
  },
};
</script>
